import {
  ApiError,
  AuthRedirectError,
  TimeoutError,
  getApiErrorDetailMessageKey,
  getApiErrorMessageKey,
  shouldSuppressErrorToast,
} from './error';

type Translator = (key: string, values?: Record<string, string | number | Date>) => string;

// 플랜 시작 거절 문구는 차단된 태스크를 지목한다. 게이트가 `PLAN_START_ENGINE_`이면 같은 값을 쓰는
// 다른 시작 문구(예: `PLAN_START_MODEL_NOT_FOUND_TASK`)가 자리표시자를 못 채워 사유가 통째로 사라지므로,
// `PLAN_START_` 전체를 대상으로 둔다. 자리표시자가 없는 문구는 넘겨받은 값을 그냥 무시한다.
const getApiErrorDetailValues = (error: ApiError): Record<string, string | number | Date> | undefined => {
  if (
    !error.errorDetailCode?.startsWith('PLAN_START_') ||
    error.blockedTaskNumber === undefined ||
    !error.blockedTaskTitle
  ) {
    return undefined;
  }

  return {
    taskNumber: error.blockedTaskNumber,
    taskTitle: error.blockedTaskTitle,
    engine: error.blockedEngine ?? '-',
    model: error.blockedModel ?? '-',
  };
};

// API/HTTP contract failures are localized only from the apiErrors namespace.
// Client-side validation and browser/local action failures belong to uiErrors.
export function resolveApiErrorToastMessage(t: Translator, error: unknown): string | null {
  if (shouldSuppressErrorToast(error)) return null;

  if (error instanceof AuthRedirectError) {
    return null;
  }

  if (error instanceof TimeoutError) {
    return t('apiErrors.REQUEST_TIMEOUT');
  }

  if (error instanceof ApiError) {
    const detailKey = getApiErrorDetailMessageKey(error);
    if (detailKey) {
      const localizedDetailMessage = t(detailKey, getApiErrorDetailValues(error));
      if (localizedDetailMessage !== detailKey && localizedDetailMessage !== '') {
        return localizedDetailMessage;
      }
    }

    const key = getApiErrorMessageKey(error);
    if (key === 'apiErrors.AUTH_REDIRECT') return null;

    const localizedMessage = t(key);
    if (localizedMessage !== key && localizedMessage !== '') {
      return localizedMessage;
    }

    return t('apiErrors.UNKNOWN');
  }

  if (error instanceof Error) {
    const plainMessage = error.message.trim();
    return plainMessage.length > 0 ? plainMessage : t('apiErrors.UNKNOWN');
  }

  return t('apiErrors.UNKNOWN');
}

// Screen-level error states use the same API/HTTP interpretation, but do not
// apply action-specific fallback copy from the uiErrors namespace.
export function resolveApiScreenErrorMessage(t: Translator, error: unknown): string | null {
  return resolveApiErrorToastMessage(t, error);
}

// Preserve action-specific fallback copy when the API layer can only resolve to UNKNOWN.
export function resolveApiErrorToastMessageOrFallback(
  t: Translator,
  error: unknown,
  fallbackMessage: string,
): string | null {
  if (shouldSuppressErrorToast(error)) {
    return null;
  }

  const message = resolveApiErrorToastMessage(t, error);
  if (!message) {
    return null;
  }

  const unknownMessage = t('apiErrors.UNKNOWN');
  return message === unknownMessage ? fallbackMessage : message;
}

// Plan task dependency cycle errors with task numbers format a specific message with cycle task numbers.
// Returns null if the error is not a cycle error or if cycle task numbers are missing, allowing fallback to general toast resolution.
export function resolvePlanTaskDependencyCycleToastMessage(t: Translator, error: unknown): string | null {
  if (shouldSuppressErrorToast(error)) {
    return null;
  }

  if (
    error instanceof ApiError &&
    error.errorDetailCode === 'PLAN_TASK_DEPENDENCY_CYCLE' &&
    Array.isArray(error.cycleTaskNumbers) &&
    error.cycleTaskNumbers.length > 0
  ) {
    const tasks = error.cycleTaskNumbers.join(', ');
    const detailKey = 'apiErrorDetails.PLAN_TASK_DEPENDENCY_CYCLE_TASKS';
    const localizedMessage = t(detailKey, { tasks });
    if (localizedMessage !== detailKey && localizedMessage !== '') {
      return localizedMessage;
    }
  }

  return null;
}
